import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

/**
 * O único jeito de um estado virar cor na tela.
 *
 * O sistema tem QUATRO tons semânticos, mais o neutro (Rizoma_UI_Spec.md §3.3).
 * Antes desta casca cada tela escrevia o seu: `STATUS_CHIP` no Financeiro,
 * pares de classe fixa no detalhe da venda, outro par na Irrigação. Aqui o
 * mapeamento é um só.
 *
 * **A invariante que este componente existe para proteger:** eixos
 * independentes rendem badges independentes. "concluída" + "divergência de 40%"
 * é um estado válido e mostra DOIS badges, lado a lado. Fundir os dois num badge
 * só — que foi a tentação toda vez — apaga uma das duas informações. (O caso que
 * batizou a regra foi `status` × `sync_status` do Manejo; o eixo de sync morreu
 * com o conector Aegro em 08/2026, a regra não.)
 *
 * Estado também nunca vira borda nem fundo de card: vai no badge, e o badge
 * mora dentro do card (ver `StatCard.legenda` e `DataCard.estado`).
 */
export type Tom = "sucesso" | "atencao" | "erro" | "info" | "neutro";

export interface StatusBadgeProps {
  tom: Tom;
  /** Círculo de 6px na cor do tom, antes do texto. */
  ponto?: boolean;
  children: React.ReactNode;
  className?: string;
  testId?: string;
}

/** O ponto herda a cor do texto do badge — um lugar só define o tom. */
const PONTO = "h-1.5 w-1.5 rounded-full bg-current shrink-0";

export function StatusBadge({ tom, ponto, children, className, testId }: StatusBadgeProps) {
  return (
    <Badge variant={tom} className={cn("gap-1.5", className)} data-testid={testId}>
      {ponto && <span className={PONTO} />}
      {children}
    </Badge>
  );
}

/**
 * De-para do vocabulário do domínio para o tom, conforme a tabela do spec.
 * Usado onde o estado vem do banco como string livre; onde a tela já sabe o
 * tom, passe `tom` direto e não chame isto.
 */
const TOM_POR_ESTADO: Record<string, Tom> = {
  // sucesso
  PAGA: "sucesso", PAGO: "sucesso", OK: "sucesso", ok: "sucesso",
  SINCRONIZADA: "sucesso", ATIVO: "sucesso", ATIVA: "sucesso",
  CONCLUIDA: "sucesso", DONE: "sucesso", FECHADA: "sucesso",
  // atenção
  A_RECEBER: "atencao", DIVERGENTE: "atencao", divergente: "atencao",
  PENDENTE: "atencao", ABERTA: "atencao", SEM_VALOR: "atencao",
  // erro
  CANCELADA: "erro", VENCIDA: "erro", FALHOU: "erro", ERRO: "erro",
  BLOQUEADA: "erro",
  // info
  EM_ANDAMENTO: "info", HOJE: "info", RASCUNHO: "info", EMITIDA: "info",
  // neutro
  INATIVO: "neutro", ARQUIVADO: "neutro", SEM_FITA: "neutro",
};

/** Cai em `neutro` quando o estado não está mapeado — nunca inventa cor. */
export function tomDoEstado(estado: string | null | undefined): Tom {
  if (!estado) return "neutro";
  return TOM_POR_ESTADO[estado] ?? TOM_POR_ESTADO[estado.toUpperCase()] ?? "neutro";
}
